import { useEffect, useState } from "react";
import { useTablesContext } from "../../contexts/tablesContextProvider";
import { BackArrow } from "../../svg/back-arrow";
import { NextArrow } from "../../svg/next-arrow";

/** Local or server-controlled pagination configuration. */
interface PaginationProps {
  itemsCount: number;
  itemsPerPage: number;
  pagesPerView: number;
  sendStartAndEndIndex: (
    startIndex: number,
    endIndex: number,
    data?: any[],
  ) => void;
  triggerHandlePageChange?: number;
  tableName: string;
  isPreviousButtonDisabled?: boolean;
  currentPage?: number;
  serverSide?: boolean;
  onPageChange?: (page: number) => void;
}

/**
 * Page controls for RisaCustomTable. Persists active page indices into
 * TablesContext so remounts restore the visible window.
 *
 * @param props - Item counts, page size, and table name
 * @returns Pagination controls
 */
export default function Pagination(props: PaginationProps) {
  const {
    getTableDataFromContext: getTableData,
    setTableDataForContext: setTableData,
  } = useTablesContext();
  const [PagesArray, setPagesArray] = useState<number[][]>([]);
  const [ActivePageArrayIndex, setActivePageArrayIndex] = useState<number>(0);
  const [ActivePage, setActivePage] = useState<number>(1);
  const pages = Math.ceil(props.itemsCount / props.itemsPerPage);

  useEffect(() => {
    if (props?.tableName) {
      setActivePage(
        props.currentPage !== undefined
          ? props.currentPage + 1
          : getTableData(props?.tableName)["currentStartPage"] || 1,
      );
      setActivePageArrayIndex(
        props.currentPage !== undefined
          ? Math.floor(props.currentPage / props.pagesPerView)
          : getTableData(props?.tableName)["currentActiveArrayIndex"] || 0,
      );
    }
  }, [props?.tableName]);

  useEffect(() => {
    if (props.currentPage === undefined) return;
    setActivePage(props.currentPage + 1);
    setActivePageArrayIndex(Math.floor(props.currentPage / props.pagesPerView));
  }, [props.currentPage, props.pagesPerView]);

  useEffect(() => {
    if (props.itemsCount > 0) {
      const results = generatePagesArray(pages, props.pagesPerView);

      const startIndex = props.serverSide
        ? 0
        : (ActivePage - 1) * props.itemsPerPage;

      const endIndex = props.serverSide
        ? props.itemsPerPage - 1
        : ActivePage * props.itemsPerPage - 1 > props.itemsCount
          ? props.itemsCount - 1
          : ActivePage * props.itemsPerPage - 1;

      setTableData(props?.tableName, {
        currentStartPage: ActivePage,
        startIndex,
        endIndex,
      });

      setPagesArray(results);
      if (props.sendStartAndEndIndex) {
        props.sendStartAndEndIndex(startIndex, endIndex);
      }
    }
  }, [props.itemsCount, ActivePage]);

  useEffect(() => {
    if (props.triggerHandlePageChange && props.triggerHandlePageChange > 0) {
      setActivePage(1);
      setActivePageArrayIndex(0);
      setTableData(props.tableName, {
        currentStartPage: 1,
        currentActiveArrayIndex: 0,
      });
    }
  }, [props.triggerHandlePageChange]);

  const generatePagesArray = (pageCount: number, size: number) => {
    if (pageCount < size) {
      return [Array.from({ length: pageCount }, (_, index) => index + 1)];
    }
    const sequenceArray = Array.from(
      { length: pageCount },
      (_, index) => index + 1,
    );
    const result: number[][] = [];
    for (let i = 0; i < sequenceArray.length; i += size) {
      result.push(sequenceArray.slice(i, i + size));
    }
    return result;
  };

  /**
   * Select a page and notify a server-controlled table when configured.
   *
   * @param page - Current or directly selected one-based page
   * @param type - Optional previous/next navigation direction
   * @returns Nothing
   */
  const handlePageChange = (page: number, type?: string): void => {
    const nextPage =
      type === "previous" ? page - 1 : type === "next" ? page + 1 : page;
    if (nextPage < 1 || nextPage > pages) return;

    const nextArrayIndex = Math.floor((nextPage - 1) / props.pagesPerView);
    setActivePage(nextPage);
    setActivePageArrayIndex(nextArrayIndex);
    setTableData(props.tableName, {
      currentActiveArrayIndex: nextArrayIndex,
      currentStartPage: nextPage,
    });
    props.onPageChange?.(nextPage - 1);
  };

  return (
    <div className="pagination-table-container">
      <div
        className={
          ActivePage === 1 || props.isPreviousButtonDisabled
            ? "previous disabled"
            : "previous"
        }
        onClick={() => handlePageChange(ActivePage, "previous")}
      >
        <BackArrow />
        Previous
      </div>
      {PagesArray[ActivePageArrayIndex]?.map((page) => (
        <div
          key={`page-${page}`}
          className={ActivePage === page ? "pages active" : "pages"}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </div>
      ))}

      <div
        className={ActivePage === pages ? "next disabled" : "next"}
        onClick={() => handlePageChange(ActivePage, "next")}
      >
        Next
        <NextArrow />
      </div>
    </div>
  );
}
